import * as React from 'react';
import { Dispatch } from 'react';
import { ButtonMultiSelect } from './button-multi-select';
import { LockedMediaTraderFields } from './locked-media-trader-fields';
import { Section } from './section';
import { UnitCostField } from './unit-cost-field';
import {
  CommonAction,
  CommonReducerState
} from '@/features/line-items/line-item-form/common/form-reducer';
import {
  selectAvailablePlatforms,
  selectDisplayCPM,
  selectIsLocked,
  selectTacticName,
  selectUnitPriceTypeName
} from '@/features/line-items/line-item-form/common/selectors';

type Option<V> = { value: V; label: string };

type MediaTeamSectionProps = {
  state: CommonReducerState;
  dispatch: Dispatch<CommonAction>;
  errorFor: (field: string) => string | undefined;
  calculatedFields: React.ReactNode;
};

export function MediaTeamSection({
  state,
  dispatch,
  errorFor,
  calculatedFields
}: MediaTeamSectionProps) {
  const isLocked = selectIsLocked(state);
  const unitPriceTypeId = state.formData.unit_price_type_id ?? undefined;
  const isCPM = selectUnitPriceTypeName(state) === 'CPM';
  const plannedUnitCostDisplay = selectDisplayCPM(state, state.formData.planned_unit_cost);
  const tacticId = state.formData.tactic_id;
  const tacticName = selectTacticName(state);
  const platformOptions = toOptions(selectAvailablePlatforms(state));
  const mediaPlatformIds = state.formData.media_platform_ids ?? [];

  return (
    <Section heading="Media Team">
      {calculatedFields}
      {isLocked ? (
        <LockedMediaTraderFields
          mediaPlatformIds={mediaPlatformIds}
          plannedUnitCost={state.formData.planned_unit_cost}
          unitPriceTypeId={unitPriceTypeId}
          onMediaUpdateClick={() =>
            // eslint-disable-next-line no-console
            console.info('[v3 dev] MediaUpdateButton clicked — navigation stub')
          }
        />
      ) : (
        <>
          <UnitCostField
            unitPriceTypeId={unitPriceTypeId}
            value={plannedUnitCostDisplay}
            onChange={value =>
              dispatch({
                type: 'SET_PLANNED_UNIT_COST',
                value: value === undefined ? undefined : isCPM ? value / 1000 : value
              })
            }
            onBlur={() => dispatch({ type: 'BLUR_FIELD', field: 'planned_unit_cost' })}
            error={errorFor('planned_unit_cost')}
          />
          <ButtonMultiSelect<number>
            id="line-item-media-platforms"
            label="Media Platform(s) (Optional)"
            placeholder="Select Tactic for options"
            description={
              platformOptions.length === 1
                ? `Only one platform option${tacticName ? ` for ${tacticName}` : ''}`
                : `Select all approved platforms${tacticName ? ` for ${tacticName}` : ''}`
            }
            value={mediaPlatformIds}
            options={platformOptions}
            onChange={ids => dispatch({ type: 'SET_MEDIA_PLATFORMS', media_platform_ids: ids })}
            disabled={tacticId == null}
            error={errorFor('media_platform_ids')}
          />
        </>
      )}
    </Section>
  );
}

function toOptions<V>(items: readonly { id: V; name: string }[]): Option<V>[] {
  return items.map(item => ({ value: item.id, label: item.name }));
}
